import { useSyncExternalStore } from "react";

/**
 * Light / dark / follow-the-system.
 *
 * The class is what does the work: globals.css declares the dark palette under
 * `.dark`, and because the tokens are semantic (bg-card, text-foreground) the
 * whole app re-tints from that one block — no `dark:` variant in any component.
 *
 * Stored per browser rather than on the account: it is a property of the screen
 * you are looking at, not of who you are. Somebody on a bright laptop and a dark
 * phone wants different answers, and syncing it would fight them.
 */
export type ColorScheme = "light" | "dark" | "system";

const STORAGE_KEY = "moneylights-color-scheme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

const isColorScheme = (value: unknown): value is ColorScheme =>
  value === "light" || value === "dark" || value === "system";

/**
 * MoneyLights is dark by default, whatever the OS says.
 *
 * "system" would be the polite default and it is what this returned at first —
 * matching the room you are sitting in is a good instinct for a tool. It is the
 * wrong one for this product: dark is the look, the auth screens force it
 * regardless, and following the OS meant most people met a light app that the
 * brand was not designed around. A default is a statement about what the product
 * IS; "system" declines to make one.
 *
 * Anyone who prefers otherwise says so once in Settings and is never asked again.
 */
export const readColorScheme = (): ColorScheme => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isColorScheme(stored)) {
      return stored;
    }
  } catch {
    // Private mode / disabled storage: the default still applies.
  }
  return "dark";
};

/** Null in embedded webviews that ship without matchMedia; booting is not optional. */
const darkMedia = (): MediaQueryList | null => {
  try {
    return window.matchMedia(DARK_QUERY);
  } catch {
    return null;
  }
};

const prefersDark = () => darkMedia()?.matches ?? false;

/** Whether `scheme` means dark right now. Only "system" depends on the OS. */
export const resolvesToDark = (scheme: ColorScheme) =>
  scheme === "dark" || (scheme === "system" && prefersDark());

/**
 * Put the class on <html>, not on a wrapper: the palette is declared on `.dark`
 * and read through the ancestor, and portalled UI (dialogs, toasts, the command
 * palette) mounts on <body> where a wrapper class would never reach it.
 *
 * Exported so index.jsx can apply the stored preference before the first paint —
 * doing it in a component would flash the wrong palette for a frame.
 */
export const applyColorScheme = (scheme: ColorScheme) => {
  const dark = resolvesToDark(scheme);
  document.documentElement.classList.toggle("dark", dark);
  // Older Safari + Chrome/Android tint the browser chrome / status bar from this,
  // so it has to track the app's PLANE (--background), not the old green nav that
  // the mobile shell no longer paints. Safari 26 ignores theme-color and instead
  // samples the sticky .ml-mtopbar (see ml-mobile-shell.css); both paths have to
  // agree, and the only way to guarantee that is to read the same token.
  //
  // It used to hold "#03100e" / "#e9f1ed" as literals, and that is exactly what
  // went stale: the plane became #060606 with the Brandbook and the iOS status
  // bar stayed on the retired green-cast black. Nothing pointed at it, because a
  // duplicated value looks intentional.
  //
  // The class is toggled above, so the computed --background is already the one
  // for this mode. jsdom does not resolve custom properties from stylesheets, so
  // the fallback is what tests see — keep it in step with §2 of globals.css.
  const plane =
    getComputedStyle(document.documentElement).getPropertyValue("--background").trim() ||
    (dark ? "#060606" : "#fafafa");
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", plane);
};

/**
 * ONE store for the whole app, not one useState per caller.
 *
 * The preference is a property of the document — there is a single <html> and a
 * single class on it — so component-local state was always a lie: SettingsView
 * flipped its own copy and the sphere, the sidebar, the mobile topbar, the drawer
 * and the toaster kept rendering art for the scheme they were mounted with. The
 * OS listener had the same shape of bug: it lived in whichever instances happened
 * to hold "system" at the time, so switching to "system" from Settings left the
 * others unsubscribed, and leaving Settings could take the last subscription with
 * it.
 *
 * useSyncExternalStore rather than a Context so nothing has to wrap the tree: the
 * store is the source of truth and every caller reads the same value.
 */
const listeners = new Set<() => void>();

let currentScheme: ColorScheme = readColorScheme();
let systemPrefersDark = prefersDark();
let media: MediaQueryList | null = null;

const notify = () => listeners.forEach((listener) => listener());

/**
 * Old embedded WebViews and Safari before 14 have matchMedia but give back a
 * MediaQueryList carrying only the deprecated addListener/removeListener pair,
 * so addEventListener there is a TypeError at mount. That used to be survivable
 * — only a caller sitting on "system" ever subscribed — but the store now
 * attaches once for everybody, so an explicit light/dark preference would take
 * the app down on exactly the devices least able to report it.
 */
const attach = (target: MediaQueryList, listener: () => void) => {
  if (typeof target.addEventListener === "function") {
    target.addEventListener("change", listener);
  } else if (typeof target.addListener === "function") {
    target.addListener(listener);
  }
};

const detach = (target: MediaQueryList, listener: () => void) => {
  if (typeof target.removeEventListener === "function") {
    target.removeEventListener("change", listener);
  } else if (typeof target.removeListener === "function") {
    target.removeListener(listener);
  }
};

/**
 * The listener is attached whatever the scheme is — one subscription, owned by
 * the store, so it cannot come and go with a component. "Only system follows the
 * OS" is still the rule; it is now enforced where it belongs, in what this does
 * with the change rather than in whether it hears about it.
 */
const onSystemChange = () => {
  const next = media?.matches ?? false;
  if (next === systemPrefersDark) {
    return;
  }
  systemPrefersDark = next;
  if (currentScheme === "system") {
    applyColorScheme("system");
  }
  notify();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    media = darkMedia();
    // Resync on attach: the OS may have flipped between module load and the first
    // mount, or while the tree was unmounted.
    systemPrefersDark = media?.matches ?? false;
    if (currentScheme === "system") {
      applyColorScheme("system");
    }
    if (media) {
      attach(media, onSystemChange);
    }
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      if (media) {
        detach(media, onSystemChange);
      }
      media = null;
    }
  };
};

const getScheme = () => currentScheme;
const getSystemPrefersDark = () => systemPrefersDark;

/**
 * Writes the preference everywhere it lives — store, storage, <html> — and tells
 * every mounted caller, not just the one that asked.
 */
export const setColorScheme = (next: ColorScheme) => {
  currentScheme = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Preference is lost on reload, but the session still honours it.
  }
  applyColorScheme(next);
  notify();
};

export const useColorScheme = () => {
  // Two subscriptions rather than one object snapshot: useSyncExternalStore
  // compares snapshots by identity, and a fresh { scheme, isDark } per read would
  // never settle.
  const scheme = useSyncExternalStore(subscribe, getScheme, getScheme);
  const osPrefersDark = useSyncExternalStore(
    subscribe,
    getSystemPrefersDark,
    getSystemPrefersDark,
  );

  // Explicit choices are taken at their word — only "system" tracks the OS.
  return {
    scheme,
    setScheme: setColorScheme,
    isDark: scheme === "system" ? osPrefersDark : scheme === "dark",
  };
};
